import React from 'react';
import { Industry } from './industries.data';

export type BookingStep = 0 | 1 | 2 | 3;

const STEP_LABELS = ['Servicio', 'Fecha & hora', 'Tus datos', 'Confirmado'];

const MONTH_NAME = 'abril';
const YEAR = 2026;

function formatColombianDate(day: number): string {
  return `${day} de ${MONTH_NAME}, ${YEAR}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Hora en 24h para ICS a partir de etiquetas tipo "7:00am" */
function slotTo24h(slot: string): { h: number; m: number } {
  const m = slot.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
  if (!m || !m[1] || !m[2] || !m[3]) return { h: 9, m: 0 };
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3].toLowerCase();
  if (ap === 'pm' && h !== 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return { h, m: min };
}

function buildIcsDownload(opts: {
  title: string;
  day: number;
  timeSlot: string;
  durationMin: number;
  business: string;
}): void {
  const { h, m } = slotTo24h(opts.timeSlot);
  const start = new Date(Date.UTC(YEAR, 3, opts.day, h + 5, m));
  const end = new Date(start.getTime() + opts.durationMin * 60 * 1000);
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Clientelo//Demo//ES',
    'BEGIN:VEVENT',
    `UID:clientelo-demo-${Date.now()}@clientelo.co`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${opts.title.replace(/,/g, '\\,')}`,
    `DESCRIPTION:Cita con ${opts.business.replace(/,/g, '\\,')} (demo Clientelo)`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'cita-clientelo-demo.ics';
  a.click();
  URL.revokeObjectURL(url);
}

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function whatsappHref(phone: string, text: string): string {
  const d = digitsOnly(phone);
  const withCc = d.length === 10 ? `57${d}` : d;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(text)}`;
}

export interface BookingModalProps {
  industry: Industry;
  inline?: boolean;
  onClose?: () => void;
  step: BookingStep;
  setStep: (s: BookingStep) => void;
  selectedService: number | null;
  setSelectedService: (n: number | null) => void;
  selectedDay: number | null;
  setSelectedDay: (n: number | null) => void;
  selectedTime: string | null;
  setSelectedTime: (t: string | null) => void;
  nombre: string;
  setNombre: (s: string) => void;
  telefono: string;
  setTelefono: (s: string) => void;
  email: string;
  setEmail: (s: string) => void;
  notas: string;
  setNotas: (s: string) => void;
  onResetBooking: () => void;
}

/** Primer día de la semana (0=dom) para abril 2026 */
function april2026StartWeekday(): number {
  return new Date(YEAR, 3, 1).getDay();
}

export const BookingModal: React.FC<BookingModalProps> = ({
  industry,
  inline,
  onClose,
  step,
  setStep,
  selectedService,
  setSelectedService,
  selectedDay,
  setSelectedDay,
  selectedTime,
  setSelectedTime,
  nombre,
  setNombre,
  telefono,
  setTelefono,
  email,
  setEmail,
  notas,
  setNotas,
  onResetBooking,
}) => {
  const startWd = april2026StartWeekday();
  const daysInMonth = 30;
  const blanks = Array.from({ length: startWd }, (_, i) => (
    <div key={`b-${i}`} className="aspect-square" />
  ));
  const service = selectedService !== null ? industry.services[selectedService] : null;
  const durationMin =
    service?.duration.includes('h') && !service.duration.includes('min')
      ? 180
      : parseInt(service?.duration.replace(/\D/g, '') || '60', 10) || 60;

  const canNextStep0 = selectedService !== null;
  const canNextStep1 = selectedDay !== null && selectedTime !== null;
  const canConfirmStep2 =
    nombre.trim().length > 1 && telefono.trim().length >= 7 && email.includes('@');

  const body = (
    <div
      key={step}
      className="transition-all duration-200 opacity-100"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>

      {step === 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Selecciona servicio</h3>
          {industry.services.map((s, i) => {
            const sel = selectedService === i;
            return (
              <button
                key={s.name}
                type="button"
                onClick={() => setSelectedService(i)}
                className={`w-full text-left rounded-lg border p-3 flex gap-3 items-center transition-all duration-200 ${
                  sel ? 'border-2 bg-opacity-10' : 'border border-gray-200 hover:border-gray-300'
                }`}
                style={
                  sel
                    ? {
                        borderColor: industry.color,
                        backgroundColor: industry.accentLight,
                      }
                    : undefined
                }
              >
                <span className="text-2xl shrink-0">{s.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900">{s.name}</div>
                  <div className="text-sm text-gray-500">
                    {s.duration} · {s.price}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {step === 1 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Selecciona fecha y hora</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Abril {YEAR}
              </p>
              <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
                {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'].map((d) => (
                  <div key={d} className="py-1 font-medium">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {blanks}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const available = industry.availableDays.includes(day);
                  const sel = selectedDay === day;
                  return (
                    <button
                      key={day}
                      type="button"
                      disabled={!available}
                      onClick={() => {
                        setSelectedDay(day);
                        setSelectedTime(null);
                      }}
                      className={`aspect-square rounded-full text-sm relative flex flex-col items-center justify-center transition-all duration-200 ${
                        available ? 'hover:opacity-90' : 'opacity-35 pointer-events-none text-gray-300'
                      } ${sel ? 'text-white font-semibold' : 'text-gray-800'}`}
                      style={
                        sel
                          ? { backgroundColor: industry.color }
                          : available
                            ? { backgroundColor: 'transparent' }
                            : undefined
                      }
                    >
                      {day}
                      {available && !sel && (
                        <span
                          className="absolute bottom-1 w-1 h-1 rounded-full"
                          style={{ backgroundColor: industry.color }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Horarios
              </p>
              {!selectedDay ? (
                <p className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-200 rounded-lg">
                  Elige un día en el calendario
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {industry.timeSlots.map((slot) => {
                    const full = industry.fullSlots.includes(slot);
                    const sel = selectedTime === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={full}
                        onClick={() => setSelectedTime(slot)}
                        className={`py-2 px-1 rounded-lg text-xs font-medium border transition-all duration-200 ${
                          full
                            ? 'border-gray-100 bg-gray-50 text-gray-400 line-through'
                            : sel
                              ? 'text-white border-2'
                              : 'border border-gray-200 text-gray-800 hover:border-gray-300'
                        }`}
                        style={
                          sel && !full
                            ? { backgroundColor: industry.color, borderColor: industry.color }
                            : undefined
                        }
                      >
                        {full ? (
                          <>
                            {slot}
                            <span className="block text-[10px] normal-case">• lleno</span>
                          </>
                        ) : (
                          slot
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 2 && service && (
        <div className="space-y-4">
          <div
            className="rounded-lg border p-3 text-sm space-y-1"
            style={{ borderColor: industry.color, backgroundColor: industry.accentLight }}
          >
            <div className="font-semibold text-gray-900">Resumen</div>
            <div>
              <span className="text-gray-600">Servicio: </span>
              {service.icon} {service.name}
            </div>
            <div>
              <span className="text-gray-600">Fecha: </span>
              {selectedDay !== null ? formatColombianDate(selectedDay) : '—'}
            </div>
            <div>
              <span className="text-gray-600">Hora: </span>
              {selectedTime ?? '—'}
            </div>
            <div>
              <span className="text-gray-600">Duración: </span>
              {service.duration}
            </div>
            <div>
              <span className="text-gray-600">Precio: </span>
              <span className="font-semibold" style={{ color: industry.color }}>
                {service.price}
              </span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Tus datos</h3>
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Nombre completo *</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="María Pérez"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">WhatsApp / Teléfono *</span>
              <input
                type="tel"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="300 123 4567"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Correo electrónico *</span>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Notas (opcional)</span>
              <textarea
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm min-h-[80px]"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Primera vez, alergias, preferencias..."
              />
            </label>
          </div>
        </div>
      )}

      {step === 3 && service && selectedDay !== null && selectedTime && (
        <div className="text-center space-y-4">
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl"
            style={{ backgroundColor: '#dcfce7', color: '#22c55e' }}
          >
            ✓
          </div>
          <h3 className="text-xl font-bold text-gray-900">¡Cita confirmada!</h3>
          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            Recibirás un recordatorio por WhatsApp y correo 24h antes de tu cita.
          </p>
          <div
            className="rounded-xl border-2 p-4 text-left text-sm"
            style={{ borderColor: industry.color }}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{service.icon}</span>
              <span className="font-semibold text-gray-900">{service.name}</span>
            </div>
            <div className="text-gray-600">
              {formatColombianDate(selectedDay)} · {selectedTime}
            </div>
            <div className="text-gray-600 mt-1">{industry.biz}</div>
            <div className="mt-2 font-bold" style={{ color: industry.color }}>
              {service.price}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-gray-200 font-medium text-gray-800 hover:bg-gray-50 transition-all duration-200"
              onClick={() =>
                buildIcsDownload({
                  title: `${service.name} — ${industry.biz}`,
                  day: selectedDay,
                  timeSlot: selectedTime,
                  durationMin,
                  business: industry.biz,
                })
              }
            >
              📱 Agregar al calendario
            </button>
            <a
              href={whatsappHref(
                telefono,
                `Hola, confirmo mi cita: ${service.name} el ${formatColombianDate(selectedDay)} a las ${selectedTime} en ${industry.biz}.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg border border-gray-200 font-medium text-gray-800 hover:bg-gray-50 transition-all duration-200 text-center"
            >
              💬 Compartir por WhatsApp
            </a>
          </div>
          <button
            type="button"
            className="text-sm font-medium underline text-gray-600 hover:text-gray-900"
            onClick={onResetBooking}
          >
            Reservar otra cita
          </button>
        </div>
      )}
    </div>
  );

  const stepper = (
    <div className="mb-6 flex items-start justify-center gap-0 max-w-lg mx-auto">
      {STEP_LABELS.map((label, i) => {
        const done = step > i;
        const active = step === i;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center w-[72px] sm:w-20 shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-200 ${
                  done
                    ? 'border-green-500 bg-green-500 text-white'
                    : active
                      ? 'text-white'
                      : 'border-gray-200 text-gray-400 bg-white'
                }`}
                style={
                  active && !done
                    ? { backgroundColor: industry.color, borderColor: industry.color }
                    : undefined
                }
              >
                {done ? '✓' : i + 1}
              </div>
              <span
                className={`text-[10px] sm:text-xs mt-1.5 text-center leading-tight ${
                  active ? 'font-semibold text-gray-900' : 'text-gray-500'
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                className={`h-0.5 flex-1 mt-4 min-w-[12px] max-w-[40px] sm:max-w-[56px] transition-all duration-200 ${
                  step > i ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const navFooter =
    step < 3 ? (
      <div className="flex justify-between items-center pt-4 gap-2">
        {step > 0 ? (
          <button
            type="button"
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
            onClick={() => setStep((step - 1) as BookingStep)}
          >
            ← Atrás
          </button>
        ) : (
          <span />
        )}
        {step === 0 && (
          <button
            type="button"
            disabled={!canNextStep0}
            className="ml-auto px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            style={{ backgroundColor: industry.color }}
            onClick={() => setStep(1)}
          >
            Siguiente →
          </button>
        )}
        {step === 1 && (
          <button
            type="button"
            disabled={!canNextStep1}
            className="ml-auto px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            style={{ backgroundColor: industry.color }}
            onClick={() => setStep(2)}
          >
            Siguiente →
          </button>
        )}
        {step === 2 && (
          <button
            type="button"
            disabled={!canConfirmStep2}
            className="ml-auto px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            style={{ backgroundColor: industry.color }}
            onClick={() => {
              // TODO (producción): persistir en Supabase. Tabla `reservas` (ver migraciones):
              // - Flujo agenda: horario_id, cliente_id, producto_id, fecha, hora_inicio, hora_fin, estado, notas
              // - Flujo catálogo/listado: canal 'web_listado', horario_id NULL, nombre_prospecto, telefono_prospecto, mensaje
              //   (puede requerir cliente_id según políticas; crear cliente lead o RPC pública)
              // await supabase.from('reservas').insert({ empresa_id, ... })
              setStep(3);
            }}
          >
            Confirmar cita
          </button>
        )}
      </div>
    ) : null;

  const card = (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col max-h-[min(90vh,720px)]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <span className="font-semibold text-gray-900">Reservar con {industry.biz}</span>
        {!inline && onClose && (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2"
            aria-label="Cerrar"
            onClick={onClose}
          >
            ×
          </button>
        )}
      </div>
      <div className="p-4 overflow-y-auto flex-1 min-h-0">
        {stepper}
        {body}
      </div>
      {navFooter && <div className="px-4 pb-4 pt-0 shrink-0 border-t border-gray-50">{navFooter}</div>}
    </div>
  );

  if (inline) {
    return <div className="h-full flex flex-col min-h-[520px]">{card}</div>;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-all duration-200"
      role="presentation"
      onClick={onClose}
    >
      <div className="w-full max-w-lg relative" onClick={(e) => e.stopPropagation()}>
        {card}
      </div>
    </div>
  );
};
